import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { FiBox } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { CatalogImageOwner } from "../../features/floorplan3d/catalog/catalogQueries";
import { UserModel3DLibraryItem } from "../../features/floorplan3d/userModels/userModel3DTypes";
import { insertUserModel3D } from "../../features/floorplan3d/userModels/insertUserModel3D";
import {
  isPlaceableFavoriteProduct,
  useUserFavoriteProducts,
} from "../../gql/userFavorites";
import { useUserFavoriteUserModels } from "../../gql/userModelFavorites";
import { CommunityModelCard } from "./productLibrary/CommunityModelCard";
import { CommunityModelDetailModal } from "./productLibrary/CommunityModelDetailModal";
import { ProductLibraryCard } from "./productLibrary/ProductLibraryCard";
import { ProductLibraryDetailModal } from "./productLibrary/ProductLibraryDetailModal";
import { insertPublishedProduct3D } from "./productLibrary/insertPublishedProduct3D";

const PANEL_WIDTH = 600;

type FavoritesFilter = "ALL" | "BONI" | "BRAND" | "USERS";

type Props = {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
};

const FILTER_TABS: Array<{ id: FavoritesFilter; labelKey: string }> = [
  { id: "ALL", labelKey: "favorites.filterAll" },
  { id: "BONI", labelKey: "productLibrary.byBoni" },
  { id: "BRAND", labelKey: "productLibrary.byBrands" },
  { id: "USERS", labelKey: "productLibrary.byUsers" },
];

/**
 * Brand catalog 3D assets are sourced from uploads; BONI-generated assets are not.
 * Same rule as the product library origin filter on the server.
 */
const isBrandLibraryProduct = (product: CatalogImageOwner): boolean =>
  product.active3DAsset?.source === "upload";

/**
 * Editor-style CategoryLink pills for My Favorites source filters.
 */
const FavoritesFilterBar: React.FC<{
  value: FavoritesFilter;
  onChange: (filter: FavoritesFilter) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t("favorites.filterSources")}
      className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-3"
    >
      {FILTER_TABS.map((tab) => {
        const active = value === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={`flex cursor-pointer items-center rounded-md p-1 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111] ${
              active
                ? "border border-gray-300 bg-white text-gray-900"
                : "border border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            <span className="px-1 text-xs">{t(tab.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * My Favorites — placeable catalog + community 3D favorites (both account-scoped),
 * with editor-style source filters.
 */
export const FavoritesPanel: React.FC<Props> = observer(
  ({ position, onClose, dropdownRef }) => {
    const { t } = useTranslation();
    const { data, loading, error } = useUserFavoriteProducts();
    const {
      data: communityData,
      loading: communityLoading,
      error: communityError,
    } = useUserFavoriteUserModels();
    const [filter, setFilter] = React.useState<FavoritesFilter>("ALL");
    const [detailProduct, setDetailProduct] = React.useState<
      CatalogImageOwner | undefined
    >();
    const [detailCommunityModel, setDetailCommunityModel] = React.useState<
      UserModel3DLibraryItem | undefined
    >();
    const [message, setMessage] = React.useState<string | undefined>();

    const placeableFavorites = React.useMemo(
      () =>
        (data?.userFavoriteProducts ?? []).filter(isPlaceableFavoriteProduct),
      [data?.userFavoriteProducts],
    );

    const placeableCommunityFavorites = React.useMemo(
      () =>
        (communityData?.userFavoriteUserModels ?? []).filter(
          (model) => model.status === "ready" && Boolean(model.optimizedUrl),
        ),
      [communityData?.userFavoriteUserModels],
    );

    const visibleProducts = React.useMemo(() => {
      if (filter === "USERS") return [];
      if (filter === "BONI") {
        return placeableFavorites.filter((product) => !isBrandLibraryProduct(product));
      }
      if (filter === "BRAND") {
        return placeableFavorites.filter(isBrandLibraryProduct);
      }
      return placeableFavorites;
    }, [filter, placeableFavorites]);

    const visibleCommunityModels = React.useMemo(() => {
      if (filter === "BONI" || filter === "BRAND") return [];
      return placeableCommunityFavorites;
    }, [filter, placeableCommunityFavorites]);

    const totalVisible = visibleProducts.length + visibleCommunityModels.length;

    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent) => {
        if (event.key !== "Escape" || detailProduct || detailCommunityModel)
          return;
        event.preventDefault();
        onClose();
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }, [detailCommunityModel, detailProduct, onClose]);

    const setRefs = (element: HTMLDivElement | null) => {
      if (typeof dropdownRef === "function") dropdownRef(element);
      else if (dropdownRef && "current" in dropdownRef) {
        (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current =
          element;
      }
    };

    const insertProduct = (product: CatalogImageOwner) => {
      const result = insertPublishedProduct3D(product);
      if (!result?.ok) setMessage(result?.reason);
    };

    const insertCommunityModel = (model: UserModel3DLibraryItem) => {
      const result = insertUserModel3D(model);
      if (!result?.ok) setMessage(result?.reason);
    };

    // Catalog and community favorites load independently — a community outage
    // must not blank out working catalog favorites.
    const showInitialLoading =
      (loading || communityLoading) && totalVisible === 0 && !error;
    const showCatalogLoadError = Boolean(error) && placeableFavorites.length === 0;
    const showCommunityLoadError = Boolean(communityError);
    const hasNoResults =
      !loading && !communityLoading && !error && totalVisible === 0;

    return (
      <>
        <div
          ref={setRefs}
          role="dialog"
          aria-modal="true"
          aria-label={t("panels.favorites")}
          onClick={(event) => event.stopPropagation()}
          className="fixed z-50 flex min-h-0 flex-col overflow-hidden rounded-md border border-stone-200 bg-white shadow-xl max-lg:!inset-0 max-lg:!h-full max-lg:!w-full max-lg:!rounded-none max-lg:!border-0"
          style={{
            top: 16,
            bottom: 16,
            left: position.left,
            width: PANEL_WIDTH,
            maxWidth: `calc(100vw - ${position.left + 16}px)`,
          }}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-stone-200 bg-white px-4 py-3">
            <div className="text-sm font-normal">{t("panels.favorites")}</div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("favorites.close")}
              className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
            >
              <IoClose style={{ strokeWidth: 0.5 }} size={24} />
            </button>
          </div>

          <FavoritesFilterBar value={filter} onChange={setFilter} />

          <div className="flex min-h-0 flex-1 flex-col bg-[#F7F6F4]">
            {message && (
              <div
                role="status"
                className="mx-3 mt-3 flex shrink-0 items-start justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              >
                <span>{message}</span>
                <button
                  type="button"
                  onClick={() => setMessage(undefined)}
                  className="font-medium underline"
                >
                  {t("common.dismiss")}
                </button>
              </div>
            )}
            {showCatalogLoadError && (
              <div className="mx-3 mt-3 shrink-0 rounded-md border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-800">
                {t("favorites.loadError")}
              </div>
            )}
            {showCommunityLoadError && !showCatalogLoadError && (
              <div
                role="status"
                className="mx-3 mt-3 shrink-0 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              >
                {t("favorites.communityLoadError")}
              </div>
            )}
            {showInitialLoading ? (
              <div className="flex flex-1 items-center justify-center px-8 py-14 text-sm text-zinc-600">
                {t("common.loading")}
              </div>
            ) : hasNoResults ? (
              <div className="flex flex-1 flex-col items-center justify-center px-8 py-14 text-center">
                <FiBox size={30} className="text-zinc-500" />
                <h3 className="mt-3 text-sm font-semibold text-zinc-900">
                  {t("favorites.emptyTitle")}
                </h3>
                <p className="mt-1 max-w-sm text-sm text-zinc-600">
                  {t("favorites.emptyHint")}
                </p>
              </div>
            ) : (
              <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-2 content-start items-start gap-2 overflow-y-auto overscroll-contain p-2 pb-8 sm:gap-3 sm:p-3 lg:gap-4">
                {visibleProducts.map((product) => (
                  <ProductLibraryCard
                    key={product.id}
                    product={product}
                    onInsert={insertProduct}
                    onMoreInfo={setDetailProduct}
                  />
                ))}
                {visibleCommunityModels.map((model) => (
                  <CommunityModelCard
                    key={`community-${model.id}`}
                    model={model}
                    onInsert={insertCommunityModel}
                    onMoreInfo={setDetailCommunityModel}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {detailProduct && (
          <ProductLibraryDetailModal
            product={detailProduct}
            onClose={() => setDetailProduct(undefined)}
          />
        )}
        {detailCommunityModel && (
          <CommunityModelDetailModal
            model={detailCommunityModel}
            onClose={() => setDetailCommunityModel(undefined)}
          />
        )}
      </>
    );
  },
);
